import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom'
import CONSTANT from '@/constant'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { useCallback, useContext, useEffect } from 'react'
import dayjs from 'dayjs'
import { useGetClaimDetail } from '@/services/healthClaim/useGetClaimDetail'
import healthClaimFormAtom from '@/recoil/healthClaimForm'
import { DocumentDispatchContext } from './HealthClaimDetailPage'
import { DocumentItem } from './SupportingDocumentsForClaimTabDocument/types'
import { useSupportingDocumentsForClaim } from '@/hooks'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { ClaimStatusCodeEnum } from '../HealthClaimPage/ClaimHandlingTable/types'
import userAtom from '@/recoil/user/atom'
import { useListHealthClaimUserApprove } from '@/services/healthClaim/useListHealthClaimUserApprove'
import ClaimActionBarHook from './ClaimActionBar/ClaimActionBar.hook'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'

const ALLOWED_STATUS_FOR_EDIT: ClaimStatusCodeEnum[] = [
  ClaimStatusCodeEnum.IN_REVIEW,
  ClaimStatusCodeEnum.DOCUMENTS_REQUESTED,
  ClaimStatusCodeEnum.PENDING_APPROVAL,
]

const HealthClaimDetailPage = () => {
  const navigate = useNavigate()
  const { claimFormId } = useParams<{ claimFormId?: string }>()
  const { initialState } = useSupportingDocumentsForClaim()
  const { data } = useGetClaimDetail(claimFormId)
  const [healthClaim, setHealthClaim] = useRecoilState(healthClaimFormAtom)
  const user = useRecoilValue(userAtom)
  const resetHealthClaim = useResetRecoilState(healthClaimFormAtom)
  const dispatch = useContext(DocumentDispatchContext)
  const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE
  const isEditMode = healthClaim.mode === HealthClaimModeEnum.EDIT
  const location = useLocation() as { state?: { referrer?: string } }
  const isOwner = data?.ownerId === user.id
  const isManager = user.permissions.includes(BACKOFFICE_USER_PERMISSION.APP010104)
  const backofficeFullname = `${user.firstname} ${user.lastname}`
  const queryApprover = useListHealthClaimUserApprove({
    search: backofficeFullname,
    page: 1,
    limit: 1,
    enabled: !!backofficeFullname,
  })
  const { isHealthClaimApprover } = ClaimActionBarHook()
  const statusCode = data?.statusCode as ClaimStatusCodeEnum | undefined
  const approver = queryApprover.data?.data?.[0]
  const approvedClaimAmount = data?.approvedClaimAmount

  const redirectToViewPage = useCallback(() => {
    if (claimFormId) {
      const nextPath = CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE
      const currentPath = CONSTANT.ROUTES.HEALTH_CLAIM_EDIT_PAGE
      navigate(generatePath(nextPath, { claimFormId }), { state: { referrer: currentPath } })
    }
  }, [claimFormId, navigate])

  // add effect to handle cannot edit and navigate to view page instead
  useEffect(() => {
    if (!isEditMode || !claimFormId || !statusCode) return

    // if in edit mode but status is not allowed, redirect to view page
    if (!ALLOWED_STATUS_FOR_EDIT.includes(statusCode)) {
      redirectToViewPage()
      return
    }

    // in PENDING_APPROVAL status, if current user cannot approve, redirect to view page
    if (statusCode === ClaimStatusCodeEnum.PENDING_APPROVAL) {
      if (isOwner) {
        redirectToViewPage()
        return
      }

      // if not owner, check if can approve
      const canApprove = approver ? isHealthClaimApprover(approver, approvedClaimAmount) : true

      if (isManager && !canApprove) {
        redirectToViewPage()
        return
      }
    }

    // in allowed status, except PENDING_APPROVAL, if current user is not owner, redirect to view page
    if (statusCode !== ClaimStatusCodeEnum.PENDING_APPROVAL && !isOwner) {
      redirectToViewPage()
      return
    }
  }, [
    claimFormId,
    approvedClaimAmount,
    approver,
    isEditMode,
    isHealthClaimApprover,
    isManager,
    isOwner,
    redirectToViewPage,
    statusCode,
  ])

  useEffect(() => {
    if (!isCreateMode) return
    const referrer = location?.state?.referrer
    const isFromReclaim =
      referrer === CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE ||
      referrer === CONSTANT.ROUTES.HEALTH_CLAIM_EDIT_PAGE

    setHealthClaim((prev) => {
      if (prev.reclaim && isFromReclaim) {
        // Preserve reclaim flow when navigated from a claim detail
        return {
          ...prev,
          mode: HealthClaimModeEnum.CREATE,
          hasRequestedDocuments: false,
          pendingRequestDocuments: [],
        }
      }
      // Fresh create: clear all old data and any reclaim/draft residue
      return {
        mode: HealthClaimModeEnum.CREATE,
        data: undefined,
        reclaim: false,
        hasRequestedDocuments: false,
        pendingRequestDocuments: [],
        draft: undefined,
      }
    })
  }, [isCreateMode, setHealthClaim, location?.state?.referrer])

  useEffect(() => {
    if (!data) {
      // If there's no claimFormId (create route), do not reset Recoil
      // to preserve reclaim/draft state across navigation.
      if (claimFormId) resetHealthClaim()
      return
    }
    setHealthClaim((prev) => ({ ...prev, data, pendingRequestDocuments: [] }))

    // TODO need to refactor
    const updatedState: DocumentItem[] = initialState.map((config) => ({
      ...config,
      claimDocuments: data?.claimDocuments?.filter(
        (claim) => claim.documentType.code === config.code,
      ),
    }))
    dispatch({ type: 'INIT', payload: updatedState })
  }, [data, dispatch, setHealthClaim, claimFormId])

  const mockHook = {
    notifyNumber: '12252700768',
    claimNumber: '12252700768',
    // status: 'submitted',
    // status: 'in_review',
    // status: 'approved',
    // status: 'rejected',
    // status: 'completed',
    // status: 'documents_requested',
    status: 'pending_approval',
    changeBy: 'ประหยัด จันโซดา',
    statusTimestamp: new Date(),
    backofficeRole: 'หัวหน้าทีม',
    // backofficeRole: 'สมาชิกทีม',
    identityId: '0044906312001',
    firstName: 'บุญสม',
    lastName: 'คงรักษ์',
    dob: dayjs('1964-02-03', 'YYYY-MM-DD'),
    phoneNumber: '0987654321',
    email: 'boonsom_mak@hotmail.com',
    policyNumber: '',
  }

  return { mockHook }
}

export default HealthClaimDetailPage
